import React, { useState, useLayoutEffect, useEffect, useMemo, useRef } from 'react';
import { DataGridPro, DataGridProProps, GridColDef, GridRenderCellParams, GridToolbar, GridInitialState, useGridApiRef } from '@mui/x-data-grid-pro';
import { useTheme, Fab, Stack, Box, IconButton, Typography, Grid, Modal, Button, Container, CircularProgress, Tooltip, FormControl, Select, MenuItem, Card, Alert } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Add as AddIcon, Cached as CachedIcon, Edit as EditIcon, Download as DownloadIcon, Grading as GradingIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAmaV2Context } from 'context/ama-v2-provider';
import SnapshotsListRowTemplate from '../snapshots-list-row-template';
import { EditMode, SnapshotStatus } from '../../../../data/enums';
import { useDynamicGridHeight } from '../../../../hooks/useDynamicGridHeight';
import { useSearchParams } from 'react-router-dom';
import { localStorageKeys, urlParams } from '../../../../data/constants';
import { AmaV2Client, AmaV2Snapshot } from 'context/ama-v2-provider/ama-v2.types';
import SnapshotDataQualityReport from '../snapshot-data-quality-report';


const GRID_VERT_OFFSET = 300;
const DELETE_POLL_INTERVAL_MS = 5000;
const DELETE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

interface ISnapshotsListProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

interface SnapshotKey {
  client_id: string;
  academic_year: string;
  snapshot_date: string;
}

export default function SnapshotsList(props: ISnapshotsListProps) {
  
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [params, setParams] = useSearchParams();
  const { snapshotsState, clientsState, loadClients, loadSnapshots, getClient, setSelectedSnapshot, deleteSnapshot, clearSnapshotsError, downloadSnapshot, selectedAcademicYear, setSelectedAcademicYear, isSnapshotActive } = useAmaV2Context();
  const [snapshotForDelete, setSnapshotForDelete] = useState<SnapshotKey | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [snapshotForDQR, setSnapshotForDQR] = useState<AmaV2Snapshot | null>(null);
  const [dqModalInitialTab, setDQModalInitialTab] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    
  const getDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => {
    if (row.status !== 'COMPLETED') return null;
    return <DetailPanelContent row={row} />;
  }, []);
  const getDetailPanelHeight = React.useCallback(() => 'auto', []);
  const safe = (v: number | undefined | null) => (v === undefined || v === null ? '-' : v);
  const formatRuntime = (v: number | undefined | null) => {
    if (v === undefined || v === null) return '-';
    const minutes = v / 60;
    return minutes.toFixed(1);
  };
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { gridHeight } = useDynamicGridHeight({ gridItems: snapshotsState.data, offset: GRID_VERT_OFFSET });

  // Cleanup polling timers on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
    };
  }, []);

  // Process deep link parameters when snapshots data loads
  useEffect(() => {
    if (snapshotsState.status !== 'success' || !snapshotsState.data.length) return;

    const clientId = params.get(urlParams.CLIENT_ID);
    const academicYear = params.get(urlParams.ACADEMIC_YEAR);
    const snapshotDate = params.get(urlParams.SNAPSHOT_DATE);
    const operation = params.get(urlParams.OPERATION) || 'modal';
    const tabParam = params.get(urlParams.TAB);

    // Check if deep link params exist
    if (!clientId || !academicYear || !snapshotDate) return;

    // Find the snapshot in loaded data
    const snapshot = snapshotsState.data.find(
      s => s.client_id === clientId && 
           s.academic_year === academicYear && 
           s.snapshot_date === snapshotDate
    );

    if (!snapshot) {
      // Snapshot not found in currently loaded data - silently ignore
      console.warn('[SnapshotsList] Deep link snapshot not found in loaded data:', { clientId, academicYear, snapshotDate });
      return;
    }

    // Execute operation based on op parameter
    if (operation === 'modal') {
      const tab = tabParam ? parseInt(tabParam, 10) : 0;
      setDQModalInitialTab(isNaN(tab) || tab < 0 ? 0 : tab);
      setSnapshotForDQR(snapshot);
    } else if (operation === 'expand') {
      // Expand detail panel for this row
      setTimeout(() => {
        try {
          const rowId = `${snapshot.client_id}-${snapshot.academic_year}-${snapshot.snapshot_date}`;
          apiRef.current?.toggleDetailPanel(rowId);
        } catch (error) {
          console.error('[SnapshotsList] Error expanding detail panel:', error);
        }
      }, 500);
    } else if (operation === 'select') {
      // Just scroll to the row
      setTimeout(() => {
        try {
          const rowIndex = snapshotsState.data.findIndex(
            s => s.client_id === snapshot.client_id && 
                 s.academic_year === snapshot.academic_year && 
                 s.snapshot_date === snapshot.snapshot_date
          );
          if (rowIndex >= 0) {
            apiRef.current?.scrollToIndexes({ rowIndex, colIndex: 0 });
          }
        } catch (error) {
          console.error('[SnapshotsList] Error scrolling to row:', error);
        }
      }, 500);
    }
  }, [snapshotsState.status, snapshotsState.data, params]);

  // Persist filter state (column filters + quick filter) in localStorage
  const persistFilterState = (opts: { filterModel?: any; quickFilterValues?: string[] }) => {
    try {
      const filterModel = opts.filterModel ?? { items: [] };
      const quickFilterValues = opts.quickFilterValues ?? [];
      const isModelEmpty = !filterModel?.items || filterModel.items.length === 0;
      const isQuickEmpty = !quickFilterValues || quickFilterValues.length === 0;
      if (isModelEmpty && isQuickEmpty) {
        localStorage.removeItem(localStorageKeys.AMA_SNAPSHOTS_GRID_STATE);
      } else {
        const saved = localStorage.getItem(localStorageKeys.AMA_SNAPSHOTS_GRID_STATE);
        const next = saved ? JSON.parse(saved) : {};
        next.filter = { filterModel, quickFilterValues };
        localStorage.setItem(localStorageKeys.AMA_SNAPSHOTS_GRID_STATE, JSON.stringify(next));
      }
    } catch (e) {
      // Intentionally ignore storage errors (e.g., Safari private mode)
    }
  };

  // useLayoutEffect(() => {

  //   // Save grid state when view unmounts
  //   return () => {
  //     if (apiRef.current && apiRef.current.exportState) {
  //       console.log(`SnapshotsList.useLayoutEffect :: saving grid state to local storage`);
  //       localStorage.setItem(
  //         localStorageKeys.AMA_SNAPSHOTS_GRID_STATE, 
  //         JSON.stringify(apiRef.current.exportState()));
  //     }
  //   };
  // }, []);

  // useLayoutEffect(() => {

  //   const selectedId = params.get(urlParams.ID);
  //   if (!selectedId || !snapshotsState.data || !apiRef.current) return;

  //   // Working around DataGridPro bug related to scrollToIndexes
  //   setTimeout(() => {
  //     try {

  //       // TODO - do we need academic year here too?
  //       const [clientName, snapshotDate] = selectedId.split('-', 2);
  //       const rowIndex = snapshotsState.data.findIndex(snapshot => snapshot.client_id === clientName && snapshot.snapshot_date === snapshotDate);
  //       if (rowIndex >= 0) apiRef.current?.scrollToIndexes({ rowIndex: rowIndex, colIndex: 0 });

  //     } catch (error) {
  //       console.error(`SnapshotsList :: error scrolling to selected item: ${error}`);
  //     }
  //   }, 500);

  // }, [snapshotsState.data]);

  const getInitialGridState = () : GridInitialState => {
    
    // Check for saved grid filters
    const gridStateStr = localStorage.getItem(localStorageKeys.AMA_SNAPSHOTS_GRID_STATE);
    
    // Init initial grid state obj
    const gridState = gridStateStr ? JSON.parse(gridStateStr) : {};

    // Check querystring for selected row
    const selectedId = params.get(urlParams.ID);
    if (selectedId && snapshotsState.data && apiRef.current) {

      try {

        const [clientName, snapshotDate] = selectedId.split('-', 2);
        const rowIndex = snapshotsState.data.findIndex(snapshot => snapshot.client_id === clientName && snapshot.snapshot_date === snapshotDate);
        if (rowIndex >= 0) {
          const detailPanel = { 'expandedRowIds': [selectedId] };
          gridState.detailPanel = detailPanel;
        }
      } catch (error) {
        console.error(`SnapshotsList.getInitialGridState :: error getting selected item: ${error}`);
      }
    
    }

    // Check querystring for grid filter - TODO
    const qsFilterStr = params.get(urlParams.AMA_GRID_FILTER);   

    // Return initial grid state
    return gridState;
  };

  const handleEditButtonClick = (clientId: string, academicYear: string, snapshotDate: string) => {
    setSelectedSnapshot(clientId, academicYear, snapshotDate);
    props.setEditMode(EditMode.Edit);
  };

  const handleAddButtonClick = () => {
    props.setEditMode(EditMode.Add);
  };

  const handleRefreshButtonClick = async () => {
    await loadClients();
    await loadSnapshots();
  };

  const handleModalOkButtonClick = async () => {
    if (!snapshotForDelete) return;
    const { client_id, academic_year, snapshot_date } = snapshotForDelete;
    setSnapshotForDelete(null);
    setIsDeleting(true);
    setDeleteError(null);
    setDeleteMessage('Please hang tight while we delete your snapshot and clean-up resources. The next most recent snapshot will be promoted to the active snapshot.');

    console.log('[SnapshotsList] Starting delete operation', { client_id, academic_year, snapshot_date });

    try {
      const success = await deleteSnapshot(client_id, academic_year, snapshot_date);
      
      if (!success) {
        // setDeleteError('Failed to initiate snapshot deletion');
        setIsDeleting(false);
        setDeleteMessage(null);
        return;
      }

      console.log('[SnapshotsList] Delete request accepted, starting polling');

      const startTime = Date.now();

      const pollForCompletion = async () => {
        try {
          console.log('[SnapshotsList] Polling for completion...');
          const client = await getClient(client_id);
          
          if (!client) {
            throw new Error('Failed to fetch client status');
          }

          const isLocked = (client as any).is_resource_lock;
          console.log('[SnapshotsList] Client resource lock status:', { client_id, is_resource_lock: isLocked });

          // If resource lock is true, keep polling
          if (isLocked === true) {
            console.log('[SnapshotsList] Resource still locked, continuing to poll...');
            return;
          }

          // Resource lock is false/undefined - operation should be complete, reload to verify
          console.log('[SnapshotsList] Resource lock cleared, reloading data...');
          
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (deleteTimeoutRef.current) {
            clearTimeout(deleteTimeoutRef.current);
            deleteTimeoutRef.current = null;
          }
          
          await loadClients();
          await loadSnapshots();
          
          console.log('[SnapshotsList] Delete operation complete');
          setIsDeleting(false);
          setDeleteMessage(null);

        } catch (err: any) {
          console.error('[SnapshotsList] Error during polling:', err);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (deleteTimeoutRef.current) {
            clearTimeout(deleteTimeoutRef.current);
            deleteTimeoutRef.current = null;
          }
          setDeleteError(err?.message || 'Error checking delete status');
          setIsDeleting(false);
          setDeleteMessage(null);
        }
      };

      pollIntervalRef.current = setInterval(pollForCompletion, DELETE_POLL_INTERVAL_MS);
      
      deleteTimeoutRef.current = setTimeout(() => {
        console.log('[SnapshotsList] Timeout triggered');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setDeleteError('Delete operation timed out. Please refresh to check status.');
        setIsDeleting(false);
        setDeleteMessage(null);
      }, DELETE_TIMEOUT_MS);

      await pollForCompletion();

    } catch (err: any) {
      console.error('[SnapshotsList] Error in delete operation:', err);
      setDeleteError(err?.message || 'Failed to delete snapshot');
      setIsDeleting(false);
      setDeleteMessage(null);
    }
  };

  const handleModalCancelButtonClick = () => {
    setSnapshotForDelete(null);
  };

  const handleDQModalCloseButtonClick = () => {
    setSnapshotForDQR(null);
    setDQModalInitialTab(0);
    // Remove deep link params from URL when modal closes
    const newParams = new URLSearchParams(params);
    newParams.delete(urlParams.CLIENT_ID);
    newParams.delete(urlParams.ACADEMIC_YEAR);
    newParams.delete(urlParams.SNAPSHOT_DATE);
    newParams.delete(urlParams.OPERATION);
    newParams.delete(urlParams.TAB);
    setParams(newParams);
    // De-select snapshot in provider when modal closes
    // setSelectedSnapshot(null, null, null);
  };  

  const handleErrorModalOkButtonClick = () => {
    clearSnapshotsError();
  };

  const handleDeleteButtonClick = async (client_id: string, academic_year: string, snapshot_date: string) => {
    setSnapshotForDelete({ client_id: client_id, academic_year: academic_year, snapshot_date: snapshot_date });
  };

  const handleDownloadButtonClick = async (snapshot: AmaV2Snapshot) => {
    setDownloadError(null);
    const downloadUrl = await downloadSnapshot(snapshot.client_id, snapshot.academic_year, snapshot.snapshot_date);
    if (!downloadUrl) {
      setDownloadError('Unable to generate a download URL for this active snapshot.');
      return;
    }

    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteErrorModalOkButtonClick = () => {
    setDeleteError(null);
  };

  const handleDownloadErrorModalOkButtonClick = () => {
    setDownloadError(null);
  };

  const CustomFooter = () => {
    return (
      <Container sx={{ height:  40 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ height: '100%', color: theme.palette.text.secondary }}>
          <Grid item xs={4}>
            <Typography variant="body2" align="left">
              Records: {snapshotsState.data?.length}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" align="center" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
            <a href=""
              aria-label="Refresh Snapshot List"
              aria-describedby='snapshot_refresh_timestamp'
              title='Refresh Snapshot List'
              onClick={(e) => { e.preventDefault(); handleRefreshButtonClick();}}>
             <CachedIcon 
                sx={{ 
                  verticalAlign: 'middle', 
                  mr: 1, mb:.3, 
                  color: theme.palette.text.disabled,
                  animation: snapshotsState.status === 'loading' || clientsState.status === 'loading' ? 'spin 1s linear infinite' : 'none', 
                }} />             
            </a>
            <span id="snapshot_refresh_timestamp"><span className='visually-hidden'>Last Refreshed: </span>{snapshotsState.lastUpdated}</span>              
            </Typography>
          </Grid>
          <Grid item xs={4} md={4}>
            &nbsp;
          </Grid>
        </Grid>
      </Container>
    );
  };

  const DetailPanelContent = ({ row }: { row: AmaV2Snapshot }) => {
    return (
      <Box sx={{ width: '100%', bgcolor: theme.palette.grey[100], p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Card sx={{ flex: 1, p: 2 }}>
            <Box component='table' sx={{ bgcolor: 'white', width: '100%', borderCollapse: 'collapse', '& td': { p: .5, fontSize: '.75rem' } }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', fontSize: '.70rem', fontWeight: 600, color: theme.palette.text.secondary }}>Attribute</th>
                  <th style={{ textAlign: 'right', fontSize: '.70rem', fontWeight: 600, color: theme.palette.text.secondary }}>Bronze</th>
                  <th style={{ textAlign: 'right', fontSize: '.70rem', fontWeight: 600, color: theme.palette.text.secondary }}>Silver</th>
                  <th style={{ textAlign: 'right', fontSize: '.70rem', fontWeight: 600, color: theme.palette.text.secondary }}>Gold</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ textAlign: 'left', fontWeight: 500 }}>Record Count</td>
                  <td style={{ textAlign: 'right' }}>{safe(row.bronze_count)}</td>
                  <td style={{ textAlign: 'right' }}>{safe(row.silver_count)}</td>
                  <td style={{ textAlign: 'right' }}>{safe(row.gold_count)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'left', fontWeight: 500 }}>Runtime (min)</td>
                  <td style={{ textAlign: 'right' }}>{formatRuntime(row.bronze_runtime_s)}</td>
                  <td style={{ textAlign: 'right' }}>{formatRuntime(row.silver_runtime_s)}</td>
                  <td style={{ textAlign: 'right' }}>{formatRuntime(row.gold_runtime_s)}</td>
                </tr>
              </tbody>
            </Box>
          </Card>
          <Card sx={{ flex: 2, p: 2 }}>
            <Typography sx={{ textAlign: 'left', fontSize: '.70rem', fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
              Notes
            </Typography>
            <Box sx={{ height: '100px', overflowY: 'auto', bgcolor: 'white', pl: 0.5, pr: 0.5, pt: 0.5, pb: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '.75rem', textAlign: 'left' }}>
                {row.notes || 'No notes available.'}
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>
    );
  };

  const RenderUploadColumn = (snapshot: AmaV2Snapshot) => {
    if (!snapshot) return null;
    return (
      <Tooltip title="Edit snapshot." key={`upload-${snapshot.client_id}-${snapshot.academic_year}-${snapshot.snapshot_date}`}>
        <IconButton 
          aria-label="Edit snapshot."
          sx={{ color: theme.palette.grey[600]}}
          onClick={() => 
            { 
              handleEditButtonClick(snapshot.client_id, snapshot.academic_year, snapshot.snapshot_date);
            }}>
          <EditIcon />
        </IconButton>
      </Tooltip>
    )
  };

  const RenderDQReportColumn = (snapshot: AmaV2Snapshot) => {
    
    const clickHandler = async () => {
      setDQModalInitialTab(0);
      setSnapshotForDQR(snapshot);
      // Set URL params when opening modal
      setParams({
        [urlParams.CLIENT_ID]: snapshot.client_id,
        [urlParams.ACADEMIC_YEAR]: snapshot.academic_year,
        [urlParams.SNAPSHOT_DATE]: snapshot.snapshot_date,
        [urlParams.OPERATION]: 'modal',
        [urlParams.TAB]: '0'
      });
    };

    return (
      <Tooltip title="View Silver Data Quality Report and Logging.">
        <IconButton 
          aria-label="View Silver Data Quality Report and Logging."
          sx={{ color: theme.palette.grey[600]}}
          disabled={false}
          onClick={clickHandler}>
          <GradingIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const RenderDownloadColumn = (snapshot: AmaV2Snapshot) => {
    const { isActive } = isSnapshotActive(snapshot);
    if (!isActive) return null;

    const client = clientsState.data.find(c => c.client_id === snapshot.client_id);
    const activeFilePath = client?.active_snapshot?.file_path;
    const canDownload = !!activeFilePath;
    const tooltipTitle = canDownload
      ? 'Download the original source files for this active snapshot.'
      : 'Download file does not exists';

    return (
      <Tooltip title={tooltipTitle} key={`download-${snapshot.client_id}-${snapshot.academic_year}-${snapshot.snapshot_date}`}>
        <span>
          <IconButton
            aria-label={canDownload ? 'Download active snapshot files.' : 'Download file does not exists'}
            sx={{
              color: canDownload ? theme.palette.grey[600] : theme.palette.grey[400],
              '&.Mui-disabled': {
                color: theme.palette.grey[400],
                opacity: 0.9,
              },
            }}
            disabled={!canDownload}
            onClick={() => {
              if (!canDownload) return;
              handleDownloadButtonClick(snapshot);
            }}>
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const RenderDeleteColumn = (snapshot: AmaV2Snapshot) => {
    if (!snapshot) return null;
    return (
      <Tooltip title="Delete snapshot record (this will remove all files from Bronze and Silver)." key={`delete-${snapshot.client_id}-${snapshot.academic_year}-${snapshot.snapshot_date}`}>
        <IconButton
          aria-label="Delete snapshot record."
          sx={{ color: theme.palette.grey[600] }}
          onClick={() => {
            handleDeleteButtonClick(snapshot.client_id, snapshot.academic_year, snapshot.snapshot_date);
          }}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const RenderClientComparisonColumn = (snapshot: AmaV2Snapshot) => {
    const isClientComparison = snapshot.client_comparison === true;
    if (!isClientComparison) return <></>;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
      </Box>
    );
  };

  const columns: Array<GridColDef> = [  
    { 
      field: 'display_name', type: 'string', headerName: 'Client Name', 
      flex: 0.25, minWidth: 110,
      resizable: true, sortable: true,
      valueGetter: (params) => {
        const clientId: string = params.row?.client_id;
        if (!clientId) return '';
        const client = clientsState.data.find(c => c.client_id === clientId);
        return client?.display_name || clientId;
      }
    }, 
    // { 
    //   field: 'display_name', type: 'string', headerName: 'Client Name', 
    //   flex: 0.18, minWidth: 110,
    //   resizable: true, sortable: true
    // },     
    { 
      field: 'academic_year', type: 'string', headerName: 'Acad Year', 
      flex: 0.12, minWidth: 85,
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: true
    }, 
    { 
      field: 'snapshot_date', type: 'date', headerName: 'Snapshot Date', 
      flex: 0.16, minWidth: 105, 
      headerAlign: 'center', align: 'center',
      valueGetter: ({ value }) => value && value.length == 8 && new Date(value.substring(0,4) + '-' + value.substring(4, 6) + '-' + value.substring(6) + 'T00:00:00'),
      valueFormatter: ({ value }) => {  if (!value) return ''; else return value.getFullYear() + '-' + ('0' + (value.getMonth() + 1)).slice(-2) + '-' + ('0' + value.getDate()).slice(-2)  },
      resizable: true, sortable: true
    },
    { 
      field: 'status', type: 'string', headerName: 'Status', 
      flex: 0.14, minWidth: 100,
      headerAlign: 'left', align: 'left', 
      resizable: true, sortable: true,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <Typography
          component="span"
          sx={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'none', color: theme.palette.text.secondary }}
        >
          {params.value ? String(params.value).toLowerCase() : ''}
        </Typography>
      )
    },
    {
      field: 'client_comparison', type: 'boolean', headerName: 'Client Comp',
      flex: 0.08, minWidth: 85,
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, boolean>) => (
        <>{RenderClientComparisonColumn(params.row as AmaV2Snapshot)}</>
      )
    },
    {
      field: 'active', type: 'string', headerName: 'Active',
      flex: 0.08, minWidth: 70,
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => {
        const snap = params.row as AmaV2Snapshot;
        const { isActive } = isSnapshotActive(snap);
        if (!isActive) return <></>;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
          </Box>
        );
      }
    },
    // { 
    //   field: 'notes', type: 'string', headerName: 'Notes', 
    //   flex: 0.3, minWidth: 180,
    //   headerAlign: 'left', align: 'left', 
    //   resizable: true, sortable: true
    // },
    { 
      field: 'QualityReport', type: 'string', headerName: 'Data Quality', 
      minWidth: 125,
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderDQReportColumn(params.row as AmaV2Snapshot) } </>)
    },
    {
      field: 'Action', type: 'string', headerName: 'Action',
      flex: 0.18, minWidth: 170,
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => {
        const snapshot = params.row as AmaV2Snapshot;
        const actions = [
          { key: 'download', element: RenderDownloadColumn(snapshot) },
          { key: 'upload', element: RenderUploadColumn(snapshot) },
          { key: 'delete', element: RenderDeleteColumn(snapshot) },
        ].filter(({ element }) => Boolean(element));

        return (
          <Box sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            pr: 1,
          }}>
            {actions.map(({ key, element }) => (
              <React.Fragment key={key}>{element}</React.Fragment>
            ))}
          </Box>
        );
      }
    }
  ];

  return (
    <Box sx={{ width: '100%', height: '100%' }}>

      {/* Loading overlay */}
      {(snapshotsState.status === 'loading' || clientsState.status === 'loading' || isDeleting) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
          }}>
          <CircularProgress />
          {!!deleteMessage && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 720 }}>
              {deleteMessage}
            </Alert>
          )}
        </Box>
      )}

      {/* Academic Year selector - floating over data grid to avoid header template complication */}
      {props.mode === EditMode.View && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
            borderRadius: 1,
            boxShadow: 0,
            px: 0,
            py: 0,
          }}>
          <Typography variant="body2" sx={{ mr: 1 }}>Academic Year:</Typography>
          <FormControl size="small" variant="outlined">
            <Select
              labelId="academic-year-label"
              id="academic-year-selector"
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value as string)}
              sx={{ minWidth: 120, height: 25 }}
            >
              <MenuItem key="default" value="2026">Default (2026)</MenuItem>
              {['2025','2026','2027','2028','2029','2030'].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Grid */}
      {snapshotsState.data && 
            
        <DataGridPro
          columns={columns}
          rows={wrapSnapshots(snapshotsState.data)}
          rowThreshold={0}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          apiRef={apiRef}
          initialState={getInitialGridState()}
          onFilterModelChange={(model) => {
            persistFilterState({ filterModel: model });
          }}
          onStateChange={(state) => {
            try {
              const filter = (state as any)?.filter;
              persistFilterState({
                filterModel: filter?.filterModel,
                quickFilterValues: filter?.quickFilterValues,
              });
            } catch (e) {
              // Ignore state persistence errors
            }
          }}
          disableDensitySelector={true}
          disableColumnResize={false}          
          disableColumnSelector={true}                  
          slots={{ toolbar: GridToolbar, footer: () => (
            <Stack justifyContent="center" alignItems="center">
              <CustomFooter />
            </Stack>) 
          }}         
          slotProps={{
            toolbar: {
              showQuickFilter: true,            
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true }
          }}}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
          }           
          sx={{
            height: gridHeight,
            minHeight: '300px',
            '& .MuiDataGrid-root': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme => theme.palette.background.paper,
              color: theme => theme.palette.text.primary,
              fontSize: '.9rem'
            },
            '& .MuiDataGrid-cell': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-row.Mui-even .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[100]
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[300]
            }
          }} 
        />
      }

      {/* Add Snapshot button */}
      <Fab color="primary" aria-label="add" 
        sx={{ position: 'absolute', bottom: '10px', right: '65px' }}
        onClick={() => handleAddButtonClick()}>
        <AddIcon />
      </Fab>   

      {/* Delete confirmation modal */}
      <Modal
        open={Boolean(snapshotForDelete)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Delete
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
            Are you sure you want to delete {snapshotForDelete?.client_id}/{snapshotForDelete?.academic_year}/{snapshotForDelete?.snapshot_date}?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" sx={{mr: 2}} onClick={handleModalOkButtonClick}>Ok</Button>
            <Button variant="outlined" color="primary" onClick={handleModalCancelButtonClick}>Cancel</Button>
          </Box>
        </Box>
      </Modal>

      {/* Data Quality Report modal */}
      <Modal
        open={Boolean(snapshotForDQR)}
        aria-labelledby="dql-report"
        aria-describedby="dql-report">
        <>
          {snapshotForDQR && (
            <SnapshotDataQualityReport
              snapshot={snapshotForDQR}
              closeModal={handleDQModalCloseButtonClick}
              initialTab={dqModalInitialTab} />
          )}
        </>
      </Modal>

      {/* Error modal */}
      <Modal
        open={Boolean(snapshotsState.error)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Error
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
            {snapshotsState.error}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" onClick={handleErrorModalOkButtonClick}>Ok</Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete error modal */}
      <Modal
        open={Boolean(deleteError)}
        aria-labelledby="delete-error-modal-title"
        aria-describedby="delete-error-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,}}>
          <Typography id="delete-error-modal-title" variant="h6" component="h2">
            Delete Error
          </Typography>
          <Typography id="delete-error-modal-description" sx={{ mt: 2, mb: 4 }}>
            {deleteError}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" onClick={handleDeleteErrorModalOkButtonClick}>Ok</Button>
          </Box>
        </Box>
      </Modal>

      {/* Download error modal */}
      <Modal
        open={Boolean(downloadError)}
        aria-labelledby="download-error-modal-title"
        aria-describedby="download-error-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,}}>
          <Typography id="download-error-modal-title" variant="h6" component="h2">
            Download Error
          </Typography>
          <Typography id="download-error-modal-description" sx={{ mt: 2, mb: 4 }}>
            {downloadError}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" onClick={handleDownloadErrorModalOkButtonClick}>Ok</Button>
          </Box>
        </Box>
      </Modal>

    </Box>
  );
}

const wrapSnapshots = (snapshots: Array<AmaV2Snapshot>) => {  
  return snapshots.map((jsonObject) => {
    const id = `${jsonObject.client_id}-${jsonObject.academic_year}-${jsonObject.snapshot_date}`;
    const newObj = { ...jsonObject, id: id, client_comparison: jsonObject.client_comparison ?? false };
    return newObj;
  });
};